import React, { useState } from 'react';
import {
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Box,
  Typography
} from '@mui/material';
import DialogModal from '../../../components/DialogModal';
import TextInputWithIcon from '../../../components/TextInputWithIcon';
import {
  StyledButton,
  ValidationText,
  ModalContentWrapper,
  StyledFormLabel
} from '../styled';
import { RollNoData } from '../types/rollNoDataType';
import { RollNoSortOption } from '../config/constants';
import { Gender } from '../../../enums';
import {
  assignRollNumbersToStudents,
  sortStudentsByLastName
} from '../utils/studentRollNoUtils';

interface GenerateRollNoModalProps {
  open: boolean;
  onClose: () => void;
  data: RollNoData[];
  school?: string;
  onGenerate: (updatedData: RollNoData[]) => void;
}

const GenerateRollNoModal: React.FC<GenerateRollNoModalProps> = ({
  open,
  onClose,
  data,
  school,
  onGenerate
}) => {
  const [seriesStart, setSeriesStart] = useState('');
  const [sortBy, setSortBy] = useState<RollNoSortOption>(
    RollNoSortOption.FEMALE_STUDENTS_FIRST
  );

  const handleClose = () => {
    setSeriesStart('');
    onClose();
  };

  const handleGenerate = () => {
    let sortedData: RollNoData[] = [];
    if (sortBy === RollNoSortOption.FEMALE_STUDENTS_FIRST) {
      const femaleStudents = data
        .filter((student) => student.gender === Gender.FEMALE)
        .sort((firstStudent, secondStudent) =>
          sortStudentsByLastName(firstStudent, secondStudent, school)
        );
      const maleStudents = data
        .filter((student) => student.gender === Gender.MALE)
        .sort((firstStudent, secondStudent) =>
          sortStudentsByLastName(firstStudent, secondStudent, school)
        );
      sortedData = [...femaleStudents, ...maleStudents];
    }
    const updatedData = assignRollNumbersToStudents(sortedData, seriesStart);
    onGenerate(updatedData);
    handleClose();
  };

  return (
    <DialogModal
      open={open}
      onClose={handleClose}
      title='Generate Roll Numbers'
      actions={
        <>
          <Button onClick={handleClose} color='primary' variant='outlined'>
            Cancel
          </Button>
          <StyledButton
            onClick={handleGenerate}
            disabled={!seriesStart || parseInt(seriesStart, 10) === 0}
          >
            Generate
          </StyledButton>
        </>
      }
    >
      <ModalContentWrapper>
        <FormControl>
          <StyledFormLabel id='sort-by-label'>Sort by</StyledFormLabel>
          <RadioGroup
            aria-labelledby='sort-by-label'
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as RollNoSortOption)}
            name='sort-by-group'
          >
            <FormControlLabel
              value={RollNoSortOption.FEMALE_STUDENTS_FIRST}
              control={<Radio />}
              label={
                <Typography>
                  Female students first, Alphabetical (Last Name)
                </Typography>
              }
            />
          </RadioGroup>
        </FormControl>

        <Box>
          <TextInputWithIcon
            label='Start roll numbers from'
            required
            name='seriesStart'
            value={seriesStart}
            onChange={(e) => {
              const val = e.target.value;
              if (/^\d*$/.test(val)) {
                setSeriesStart(val);
              }
            }}
            fullWidth
          />
          <ValidationText>Roll number cannot be zero</ValidationText>
        </Box>
      </ModalContentWrapper>
    </DialogModal>
  );
};

export default GenerateRollNoModal;
